import React, { useState, useEffect, ReactNode } from 'react';
import { Button } from "@/ui-components/ui/button";
import { Input } from "@/ui-components/ui/input";
import { Label } from "@/ui-components/ui/label";
import { 
  Info,
  LayoutTemplate,
  Loader2,
  Tag,
  Type
} from "lucide-react";
import { AspectRatio } from "../core/template.schemas";
import { 
  useTemplates2Create, 
  useTemplates2GetCategories,
  useTemplates2CreateStore 
} from "@/lib/api/requests/templates.requests";
import { CreateTemplateRequest } from "@/lib/api/interfaces/templates.interface";
import { toast } from "@/lib/toast";
import { TemplateInfoDialog } from "./TemplateInfoDialog";
import { ASPECT_RATIO_CONFIG } from '../core/aspect-ratio.config';
import { CategorySelector } from './CategorySelector';
import { CreateBaseDialog } from "@/util-components/dialog/base-dialogs/create-base-dialog";

interface CreateTemplateDialogProps {
  /**
   * The trigger element that opens the dialog (e.g., Button, Icon, etc.)
   * Optional when using controlled mode with open/onOpenChange
   */
  trigger?: ReactNode;
  /**
   * Whether the dialog is open (controlled mode)
   */
  open?: boolean;
  /**
   * Callback when dialog open state changes (controlled mode)
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Callback function called after successful template creation
   */
  onSuccess: (templateId: string) => void;
  /**
   * Whether to create a store template (Internal User only)
   */
  isStoreTemplate?: boolean;
}

/**
 * Dialog component for creating new templates
 * Allows users to specify title, aspect ratio, and category
 */
export const CreateTemplateDialog: React.FC<CreateTemplateDialogProps> = ({
  trigger,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  onSuccess,
  isStoreTemplate = false,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  
  // Use controlled state if provided, otherwise use internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;
  const [title, setTitle] = useState("");
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>("1:1");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  // API hooks
  const createTemplateMutation = useTemplates2Create();
  const createStoreTemplateMutation = useTemplates2CreateStore();
  const categoriesQuery = useTemplates2GetCategories({ enabled: open });

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setTitle("");
      setAspectRatio("1:1");
      setSelectedCategories([]);
    }
  }, [open]);

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      toast.error('Titel erforderlich', {
        description: 'Bitte geben Sie einen Titel für das Template ein.',
        duration: 3000,
      });
      return;
    }

    try {
      const payload: CreateTemplateRequest = {
        title: title.trim(),
        aspectRatio,
        categories: selectedCategories,
      };

      let createdTemplate;
      if (isStoreTemplate) {
        createdTemplate = await createStoreTemplateMutation.mutateAsync(payload);
      } else {
        createdTemplate = await createTemplateMutation.mutateAsync(payload);
      }
      
      toast.success('Template erfolgreich erstellt!', {
        description: 'Das neue Template wurde erfolgreich erstellt.',
        duration: 3000,
      });

      setOpen(false);
      onSuccess(createdTemplate.id);
    } catch (error) {
      console.error('Failed to create template:', error);
      toast.error('Fehler beim Erstellen', {
        description: 'Das Template konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
        duration: 5000,
        error,
      });
    }
  };

  const categories = categoriesQuery.data?.categories || [];
  const isLoading = createTemplateMutation.isPending;

  return (
    <>
      {trigger && React.isValidElement(trigger) ? (
        React.cloneElement(trigger as React.ReactElement<any>, {
          onClick: (e: any) => {
            setOpen(true);
            if ((trigger as any).props.onClick) {
              (trigger as any).props.onClick(e);
            }
          },
        })
      ) : trigger ? (
        <span onClick={() => setOpen(true)}>{trigger}</span>
      ) : null}

      <CreateBaseDialog
        open={open}
        onClose={() => setOpen(false)}
        icon={LayoutTemplate}
        title={
          <div className="flex items-center gap-2">
            Neues Template erstellen
            <TemplateInfoDialog
              trigger={
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
                >
                  <Info className="h-3.5 w-3.5" />
                </Button>
              }
            />
          </div>
        }
        description="Templates sind wiederverwendbare Vorlagen für Social Media Posts"
        maxWidth="lg"
        hideSubmitButton={true}
        customFooterActions={
          <Button
            type="submit"
            form="create-template-form"
            disabled={isLoading || !title.trim()}
            className="h-11 px-8 bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-all min-w-[120px]"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Erstellen...
              </>
            ) : (
              <>
                <LayoutTemplate className="h-4 w-4 mr-2" />
                Erstellen
              </>
            )}
          </Button>
        }
      >
        <div className="p-5 sm:p-6 overflow-y-auto">
          <form id="create-template-form" onSubmit={handleSubmit} className="space-y-5">
            {/* Title Input */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Type className="h-4 w-4 text-gray-500" />
                <Label htmlFor="title" className="text-sm font-medium text-gray-700">
                  Template-Titel
                </Label>
                <span className="text-xs text-red-500">*</span>
              </div>
              <Input
                id="title"
                placeholder="z.B. Spielergebnis, Geburtstag, Event..."
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={isLoading}
                className="h-11 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 transition-all"
              />
            </div>

            {/* Aspect Ratio Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium text-gray-700">
                Seitenverhältnis
              </Label>
              <div className="grid grid-cols-3 gap-3">
                {ASPECT_RATIO_CONFIG.map((ratio) => (
                  <button
                    key={ratio.value}
                    type="button"
                    onClick={() => setAspectRatio(ratio.value)}
                    disabled={isLoading}
                    className={`
                      relative p-3 rounded-xl border-2 transition-all duration-200 text-left
                      ${aspectRatio === ratio.value 
                        ? 'border-blue-500 bg-blue-50 shadow-sm' 
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }
                      ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <div className={`
                        ${aspectRatio === ratio.value ? 'text-blue-600' : 'text-gray-500'}
                      `}>
                        {ratio.icon}
                      </div>
                      <span className={`
                        text-sm font-medium
                        ${aspectRatio === ratio.value ? 'text-blue-900' : 'text-gray-900'}
                      `}>
                        {ratio.label}
                      </span>
                    </div>
                    <p className={`
                      text-xs
                      ${aspectRatio === ratio.value ? 'text-blue-600' : 'text-gray-500'}
                    `}>
                      {ratio.description}
                    </p>
                    {aspectRatio === ratio.value && (
                      <div className="absolute top-2 right-2 h-2 w-2 rounded-full bg-blue-500" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Category Selection */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Tag className="h-4 w-4 text-gray-500" />
                <Label className="text-sm font-medium text-gray-700">
                  Kategorien
                </Label>
              </div>
              <CategorySelector
                categories={categories}
                selected={selectedCategories}
                onChange={setSelectedCategories}
                isLoading={isLoading}
                mode="multiple"
                allowCreate={true}
                placeholder="Kategorien wählen oder erstellen..."
              />
            </div>
          </form>
        </div>
      </CreateBaseDialog>
    </>
  );
};
